/**
 * Tracks whether a scroll is currently in flight anywhere in the document.
 *
 * Purpose: distinguish a deliberate tap from a tap that merely halts momentum
 * scrolling. On touch surfaces (notably the app's mobile WebView), tapping a
 * fast-scrolling list to stop it lands on whatever item is under the finger and
 * dispatches a `click`; consumers gate activation on `isScrolling()` so that
 * tap stops the scroll instead of opening the item.
 *
 * Driven entirely by the platform's `scroll`/`scrollend` events — no polling
 * and no timestamp math in the read path. `scrollend` gives a precise reset;
 * the short fallback timeout is a safety net only, for engines that don't emit
 * `scrollend`, so the flag can never get stuck "on".
 *
 * One capture-phase listener observes every scrolling element (scroll does not
 * bubble, but capture reaches it), installed lazily on first read.
 *
 * References:
 *  - MDN `scrollend` event (precise "scroll finished" signal):
 *    https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollend_event
 *  - MDN `scroll` event:
 *    https://developer.mozilla.org/en-US/docs/Web/API/Element/scroll_event
 *  - iOS/WebView momentum-stop tap dispatches a `click` (the bug this guards):
 *    https://github.com/ftlabs/fastclick/issues/227
 *    https://github.com/apache/cordova-ios/issues/490
 */

// Safety-net reset for engines without `scrollend`. Kept short: it only decides
// how quickly the flag clears when the precise `scrollend` signal is absent.
const SCROLLEND_FALLBACK_MS = 120;

/**
 * The surfaces scrolling right now, each with its own fallback timer.
 *
 * Per-target rather than one global flag: several surfaces can scroll at once
 * (a list carrying momentum while the `TrendingCarousel` auto-advances), and
 * `scroll`/`scrollend` are per-element events. A single shared flag would let
 * the newest scroll overwrite the one that matters, and one element's
 * `scrollend` would clear the state for every other. Entries are always removed
 * by their own timer within `SCROLLEND_FALLBACK_MS` of their last scroll event,
 * so a detached element cannot be retained.
 */
const scrollingTargets = new Map<Node, ReturnType<typeof setTimeout>>();
let installed = false;

const forget = (target: Node): void => {
  const timer = scrollingTargets.get(target);
  if (timer !== undefined) clearTimeout(timer);
  scrollingTargets.delete(target);
};

const handleScrollEnd = (event: Event): void => {
  if (event.target instanceof Node) forget(event.target);
};

const handleScroll = (event: Event): void => {
  const { target } = event;
  if (!(target instanceof Node)) return;

  const timer = scrollingTargets.get(target);
  if (timer !== undefined) clearTimeout(timer);
  scrollingTargets.set(
    target,
    setTimeout(() => forget(target), SCROLLEND_FALLBACK_MS)
  );
};

const ensureInstalled = (): void => {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const options = { capture: true, passive: true } as const;
  window.addEventListener('scroll', handleScroll, options);
  window.addEventListener('scrollend', handleScrollEnd, options);
};

/**
 * Whether a scroll is in flight on a surface `node` sits in.
 *
 * Scoping to the node matters: the listener sees **every** scroll in the
 * document, including programmatic ones from off-screen elements (the
 * `TrendingCarousel` auto-advances every 3s even while a home tool tab covers
 * it), and a document-wide flag let those silently eat taps on unrelated cards.
 * The guard is about a tap that halts *this* surface's momentum, so only a
 * scroll on an ancestor of the tapped node counts — checked against every live
 * scroller, so a concurrent scroll elsewhere neither masks nor forges one here.
 * `Node.contains` covers the document itself, so page-level scrolls still match
 * everything.
 *
 * Called without a node it answers for the whole document, as before.
 */
const isScrolling = (node?: Node | null): boolean => {
  ensureInstalled();
  if (scrollingTargets.size === 0) return false;
  if (!node) return true;

  for (const target of scrollingTargets.keys()) {
    if (target.contains(node)) return true;
  }
  return false;
};

export default isScrolling;
